import React, { useEffect, useState } from "react";
import { getLeave, getLeaveHistory } from "../../api/leave";
import { getAdvance, getAdvanceHistory } from "../../api/advances";
import { getResignation } from "../../api/resignation";
import { getComplaint } from "../../api/complaints";
import { getAppeals } from "../../api/appeals";
import { getPenalties } from "../../api/penalties";
import { getOvertime } from "../../api/overtime";
import { getRequest, getRequestHistory } from "../../api/requests";
import { resolveNotificationTarget } from "../../utils/notificationTarget";
import { parseAdvanceReasonDetails, formatWorkplaceLabel } from "../../utils/dataMappers";
import { formatPenaltyDays, humanizePenaltyReason } from "../../utils/penaltyFormat";
import AttachmentList from "./AttachmentList";
import "./NotificationDetailModal.css";

const STATUS_LABELS = {
  PENDING: "قيد المراجعة",
  IN_PROGRESS: "قيد المعالجة",
  APPROVED: "تمت الموافقة",
  REJECTED: "مرفوض",
  CANCELLED: "ملغي",
  CLOSED: "مغلق",
  OPEN: "مفتوح",
  ESCALATED: "تم التصعيد",
  RESOLVED: "تم الحل",
  pending: "قيد المراجعة",
  approved: "تمت الموافقة",
  rejected: "مرفوض",
};

const ACTION_LABELS = {
  CREATED: "تم تقديم الطلب",
  APPROVED: "وافق",
  REJECTED: "رفض",
  GM_OVERRIDE: "موافقة استثنائية",
  STEP_ADVANCED: "تقدّم للمرحلة التالية",
  STATUS_CHANGED: "تغيير الحالة",
  CANCELLED: "ألغى الطلب",
};

const ROLE_LABELS = {
  manager: "مدير مباشر",
  team_leader: "قائد فريق",
  supervisor: "مشرف",
  branch_manager: "مدير فرع",
  regional_manager: "مدير منطقة",
  department_manager: "مدير إدارة",
  hr: "موارد بشرية",
  hr_staff: "موظف موارد بشرية",
  gm: "المدير العام",
  admin: "أدمن",
};

const LEAVE_TYPE_LABELS = {
  ANNUAL: "إجازة اعتيادية",
  SICK: "إجازة مرضية",
  CASUAL: "إجازة عارضة",
  UNPAID: "إجازة على الحساب الخاص",
  COMPENSATORY: "إجازة تعويضية",
  SPECIAL: "إجازة ممنوحة",
  EMERGENCY: "إجازة عارضة",
};

const KIND_TITLES = {
  leave: "تفاصيل طلب الإجازة",
  advance: "تفاصيل طلب السلفة",
  resignation: "تفاصيل طلب الاستقالة",
  complaint: "تفاصيل الشكوى",
  appeal: "تفاصيل التظلم",
  penalty: "تفاصيل الجزاء",
  overtime: "تفاصيل طلب الإضافي",
  request: "تفاصيل الطلب",
};

const REQUEST_TYPE_LABELS = {
  leave: "إجازة",
  advance: "سلفة",
  resignation: "استقالة",
  complaint: "شكوى",
  appeal: "تظلم",
  overtime: "إضافي",
};

const OVERTIME_SETTLEMENT_LABELS = {
  PAID: "صرف مالي",
  PAID_OT: "صرف مالي",
  COMPENSATORY: "إذن تعويضي",
};

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("ar-EG", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("ar-EG", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatAmount(amount) {
  const value = Number(amount);
  if (Number.isNaN(value)) return "—";
  return `${value.toLocaleString("ar-EG")} ج.م`;
}

function statusLabel(status) {
  if (!status) return "—";
  return STATUS_LABELS[status] || STATUS_LABELS[String(status).toUpperCase()] || status;
}

function statusClass(status) {
  const s = String(status || "").toUpperCase();
  if (s === "APPROVED" || s === "RESOLVED" || s === "CLOSED") {
    return "ess-status-approved";
  }
  if (s === "REJECTED" || s === "CANCELLED") return "ess-status-rejected";
  return "ess-status-review";
}

function DetailRow({ label, children }) {
  if (children == null || children === "" || children === "—") return null;
  return (
    <div className="notif-detail-row">
      <span className="notif-detail-label">{label}</span>
      <span className="notif-detail-value">{children}</span>
    </div>
  );
}

function attachWorkplace(detail, data) {
  if (!detail) return detail;
  const emp =
    data?.employee?.employee ||
    data?.employee ||
    data?.user?.employee ||
    data?.createdBy?.employee ||
    {};
  const branch =
    detail.branch || emp.branch?.name || data?.branchName || "";
  const department =
    detail.department ||
    emp.department?.name ||
    data?.departmentName ||
    "";
  return { ...detail, branch, department };
}

function normalizeDetail(kind, data, history = null, nextApprover = null) {
  return attachWorkplace(
    normalizeDetailCore(kind, data, history, nextApprover),
    data,
  );
}

function normalizeDetailCore(kind, data, history = null, nextApprover = null) {
  if (!data) return null;

  if (kind === "leave") {
    return {
      title: KIND_TITLES.leave,
      employeeName:
        data.employee?.fullName || data.employee?.username || "موظف",
      employeeCode: data.employee?.employeeCode,
      status: data.status,
      submittedAt: data.createdAt,
      nextApprover,
      fields: [
        {
          label: "نوع الإجازة",
          value: LEAVE_TYPE_LABELS[data.leaveType] || data.leaveType,
        },
        { label: "من", value: formatDate(data.startDate) },
        { label: "إلى", value: formatDate(data.endDate) },
        { label: "عدد الأيام", value: data.days != null ? `${data.days}` : null },
        { label: "السبب", value: data.reason },
      ],
      history,
    };
  }

  if (kind === "advance") {
    const emp = data.employee?.employee || data.employee;
    const installments = Number(data.installments ?? 1) || 1;
    const isExceptional = installments > 1;
    const { reasonText, guarantorName } = parseAdvanceReasonDetails(data.reason);
    return {
      title: KIND_TITLES.advance,
      employeeName: emp?.fullName || data.employee?.username || "موظف",
      employeeCode: emp?.employeeCode || data.employee?.employeeCode,
      department: emp?.department?.name,
      branch: emp?.branch?.name,
      status: data.status,
      submittedAt: data.createdAt,
      nextApprover,
      fields: [
        { label: "المبلغ", value: formatAmount(data.amount) },
        {
          label: "نوع السلفة",
          value: isExceptional ? "استثنائية" : "عادية",
        },
        {
          label: "عدد الأقساط",
          value: `${installments}`,
        },
        ...(isExceptional
          ? [{ label: "الضامن", value: guarantorName || "—" }]
          : []),
        { label: "السبب", value: reasonText || null },
      ],
      history,
    };
  }

  if (kind === "resignation") {
    return {
      title: KIND_TITLES.resignation,
      employeeName:
        data.employee?.fullName || data.employee?.username || "موظف",
      employeeCode: data.employee?.employeeCode,
      status: data.status,
      submittedAt: data.createdAt,
      nextApprover,
      fields: [
        { label: "آخر يوم عمل", value: formatDate(data.lastWorkingDay) },
        {
          label: "مهلة السحب حتى",
          value: formatDate(data.withdrawalDeadline),
        },
        { label: "السبب", value: data.reason },
        { label: "ملاحظات التسليم", value: data.handoverNotes },
      ],
      history,
    };
  }

  if (kind === "complaint") {
    return {
      title: KIND_TITLES.complaint,
      employeeName:
        data.employee?.fullName ||
        data.createdBy?.username ||
        data.user?.username ||
        "موظف",
      employeeCode: data.employee?.employeeCode,
      status: data.status,
      submittedAt: data.createdAt,
      nextApprover,
      fields: [
        { label: "العنوان", value: data.title },
        { label: "الوصف", value: data.description },
        { label: "الأولوية", value: data.priority },
        { label: "الفئة", value: data.category },
      ],
      history,
    };
  }

  if (kind === "appeal") {
    return {
      title: KIND_TITLES.appeal,
      employeeName:
        data.employee?.fullName || data.employeeName || data.user?.username || "موظف",
      employeeCode: data.employee?.employeeCode || data.employeeCode,
      status: data.status,
      submittedAt: data.createdAt,
      nextApprover: null,
      fields: [
        {
          label: "نوع التظلم",
          value: data.type || data.appealType || data.targetType,
        },
        {
          label: "الموضوع",
          value:
            data.subject?.typeLabel ||
            data.penalty?.typeLabel ||
            data.penalty?.type,
        },
        {
          label: "يوم الواقعة",
          value: formatDate(
            data.subject?.date ||
              data.penalty?.effectiveDate ||
              data.penalty?.date,
          ),
        },
        {
          label: "اللي حصل",
          value:
            data.subject?.whatHappened ||
            humanizePenaltyReason(data.penalty?.reason) ||
            data.description,
        },
        {
          label: "قيمة الخصم",
          value:
            data.subject?.days != null
              ? formatPenaltyDays(data.subject.days)
              : data.penalty?.days != null
                ? formatPenaltyDays(data.penalty.days)
                : data.subject?.amount != null
                  ? formatAmount(data.subject.amount)
                  : data.penalty?.amount != null
                    ? formatAmount(data.penalty.amount)
                    : null,
        },
        { label: "سبب التظلم", value: data.reason || data.message || data.body },
        {
          label: "الرد",
          value: data.response || data.responseNotes || data.managerResponse,
        },
      ],
      history: null,
    };
  }

  if (kind === "penalty") {
    return {
      title: KIND_TITLES.penalty,
      employeeName:
        data.employee?.fullName || data.employeeName || "موظف",
      employeeCode: data.employee?.employeeCode || data.employeeCode,
      status: data.status || "ACTIVE",
      submittedAt: data.createdAt,
      nextApprover: null,
      fields: [
        { label: "نوع الجزاء", value: data.typeLabel || data.type || data.penaltyType },
        {
          label: "عدد الأيام",
          value: data.days != null && Number(data.days) > 0 ? `${data.days}` : null,
        },
        { label: "السبب", value: humanizePenaltyReason(data.reason || data.description) || null },
        {
          label: "المبلغ / الخصم",
          value: data.amount != null ? formatAmount(data.amount) : null,
        },
      ],
      history: null,
      attachmentEntityType: "penalty",
      attachmentEntityId: data.id,
    };
  }

  if (kind === "overtime") {
    return {
      title: KIND_TITLES.overtime,
      employeeName:
        data.employee?.fullName ||
        data.employeeName ||
        data.user?.employee?.fullName ||
        data.user?.username ||
        "موظف",
      employeeCode:
        data.employee?.employeeCode ||
        data.employeeCode ||
        data.user?.employee?.employeeCode,
      status: data.status,
      submittedAt: data.createdAt,
      nextApprover,
      fields: [
        {
          label: "عدد الساعات",
          value: data.hours != null ? `${data.hours}` : null,
        },
        { label: "من", value: formatDate(data.startDate) },
        { label: "إلى", value: formatDate(data.endDate) },
        { label: "السبب", value: data.reason },
        {
          label: "رغبة الموظف",
          value: data.requestedSettlement
            ? OVERTIME_SETTLEMENT_LABELS[data.requestedSettlement] ||
              data.requestedSettlement
            : null,
        },
        {
          label: "التسوية",
          value: data.settlement
            ? OVERTIME_SETTLEMENT_LABELS[data.settlement] || data.settlement
            : null,
        },
      ],
      history,
    };
  }

  // generic request
  const request = data.request || data;
  const payload =
    request.payload && typeof request.payload === "object"
      ? request.payload
      : {};
  const typeKey = request.type || data.requestType;
  const creator = request.createdBy || data.createdBy;
  return {
    title: KIND_TITLES.request,
    employeeName:
      creator?.employee?.fullName ||
      data.employee?.fullName ||
      creator?.username ||
      data.createdBy?.username ||
      "موظف",
    employeeCode:
      creator?.employee?.employeeCode || data.employee?.employeeCode,
    status: request.status || data.status,
    submittedAt: request.createdAt || data.createdAt,
    nextApprover,
    fields: [
      {
        label: "نوع الطلب",
        value: REQUEST_TYPE_LABELS[typeKey] || typeKey,
      },
      { label: "العنوان", value: request.title },
      ...Object.entries(payload)
        .filter(([, v]) => v != null && typeof v !== "object")
        .slice(0, 8)
        .map(([k, v]) => ({ label: k, value: String(v) })),
    ],
    history,
  };
}

function mapHistoryItems(history) {
  if (!history) return [];
  const actions = history.workflowActions || history.auditLogs || history;
  if (!Array.isArray(actions)) return [];
  return actions.map((item, index) => {
    const roleSlug = item.role?.slug || item.roleSlug;
    const roleName =
      item.roleName ||
      item.role?.name ||
      (roleSlug ? ROLE_LABELS[roleSlug] : null);
    const actionKey = item.action || item.type;
    return {
      id: item.id || index,
      action: ACTION_LABELS[actionKey] || actionKey || "إجراء",
      role: roleName,
      notes: item.notes || item.comment || item.message,
      by: resolvePerformerName(item),
      at: item.createdAt,
    };
  });
}

function resolvePerformerName(item) {
  const performer = item.performedBy;
  if (!performer) {
    return item.role?.name || null;
  }
  return (
    performer.employee?.fullName ||
    performer.fullName ||
    performer.username ||
    performer.email ||
    item.role?.name ||
    null
  );
}

async function loadTargetDetails(target) {
  if (!target) return null;

  if (target.kind === "leave") {
    const [detail, history] = await Promise.all([
      getLeave(target.id),
      getLeaveHistory(target.id).catch(() => null),
    ]);
    return normalizeDetail(
      "leave",
      detail,
      mapHistoryItems(history),
      detail?.nextApprover,
    );
  }

  if (target.kind === "advance") {
    const [detail, history] = await Promise.all([
      getAdvance(target.id),
      getAdvanceHistory(target.id).catch(() => null),
    ]);
    return normalizeDetail(
      "advance",
      detail,
      mapHistoryItems(history),
      detail?.nextApprover,
    );
  }

  if (target.kind === "resignation") {
    const detail = await getResignation(target.id);
    return normalizeDetail(
      "resignation",
      detail,
      null,
      detail?.nextApprover,
    );
  }

  if (target.kind === "complaint") {
    const detail = await getComplaint(target.id);
    return normalizeDetail(
      "complaint",
      detail,
      null,
      detail?.nextApprover,
    );
  }

  if (target.kind === "appeal") {
    const rows = await getAppeals();
    const list = Array.isArray(rows) ? rows : rows?.items || [];
    const detail = list.find((a) => String(a.id) === String(target.id));
    if (!detail) throw new Error("التظلم غير موجود");
    return normalizeDetail("appeal", detail);
  }

  if (target.kind === "penalty") {
    const rows = await getPenalties();
    const list = Array.isArray(rows) ? rows : rows?.items || [];
    const detail = list.find((p) => String(p.id) === String(target.id));
    if (!detail) throw new Error("الجزاء غير موجود");
    return normalizeDetail("penalty", detail);
  }

  if (target.kind === "overtime") {
    const detail = await getOvertime(target.id);
    const historyRequestId = target.requestId || detail?.requestId;
    const history = historyRequestId
      ? await getRequestHistory(historyRequestId).catch(() => null)
      : null;
    return normalizeDetail(
      "overtime",
      detail,
      mapHistoryItems(history),
      detail?.nextApprover,
    );
  }

  // generic request — try domain endpoints first when type is known
  const requestType = String(target.requestType || "").toLowerCase();
  if (requestType.includes("leave")) {
    try {
      const detail = await getLeave(target.id);
      const history = await getLeaveHistory(target.id).catch(() => null);
      return normalizeDetail(
        "leave",
        detail,
        mapHistoryItems(history),
        detail?.nextApprover,
      );
    } catch {
      /* fall through */
    }
  }
  if (requestType.includes("advance")) {
    try {
      const detail = await getAdvance(target.id);
      return normalizeDetail("advance", detail, null, detail?.nextApprover);
    } catch {
      /* fall through */
    }
  }
  if (requestType.includes("overtime")) {
    try {
      const detail = await getOvertime(target.id);
      const historyRequestId = target.requestId || detail?.requestId;
      const history = historyRequestId
        ? await getRequestHistory(historyRequestId).catch(() => null)
        : null;
      return normalizeDetail(
        "overtime",
        detail,
        mapHistoryItems(history),
        detail?.nextApprover,
      );
    } catch {
      /* fall through */
    }
  }

  const requestId = target.requestId || target.id;
  const [statusPayload, history] = await Promise.all([
    getRequest(requestId),
    getRequestHistory(requestId).catch(() => null),
  ]);
  return normalizeDetail(
    "request",
    statusPayload,
    mapHistoryItems(history),
    statusPayload?.nextApprover,
  );
}

function formatNextApprover(nextApprover) {
  if (!nextApprover) return null;
  if (typeof nextApprover === "string") return nextApprover;
  return (
    nextApprover.roleName ||
    nextApprover.role?.name ||
    nextApprover.slug ||
    nextApprover.approverRole?.name ||
    null
  );
}

export default function NotificationDetailModal({ notification, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      const target = resolveNotificationTarget(notification);
      if (!target) {
        setError("لا توجد تفاصيل مرتبطة بهذا الإشعار");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");
      setDetail(null);

      try {
        const result = await loadTargetDetails(target);
        if (!cancelled) setDetail(result);
      } catch (err) {
        if (cancelled) return;
        const message = err.response?.data?.message;
        setError(
          Array.isArray(message)
            ? message.join("\n")
            : message || err.message || "تعذر جلب تفاصيل الطلب",
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [notification]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose?.();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const nextApproverLabel = formatNextApprover(detail?.nextApprover);

  return (
    <div
      className="notif-detail-overlay"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="notif-detail-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="notif-detail-title"
      >
        <div className="notif-detail-header">
          <div>
            <h2 id="notif-detail-title" className="notif-detail-title">
              {detail?.title || "تفاصيل الإشعار"}
            </h2>
            {notification?.title && (
              <p className="notif-detail-subtitle">{notification.title}</p>
            )}
          </div>
          <button
            type="button"
            className="notif-detail-close"
            onClick={onClose}
            aria-label="إغلاق"
          >
            ×
          </button>
        </div>

        {loading && (
          <p className="notif-detail-loading">جاري تحميل التفاصيل...</p>
        )}

        {!loading && error && (
          <div className="notif-detail-error">
            <p>{error}</p>
            {notification?.message && (
              <div className="notif-detail-fallback">
                <DetailRow label="الإشعار">{notification.message}</DetailRow>
              </div>
            )}
          </div>
        )}

        {!loading && !error && detail && (
          <div className="notif-detail-body">
            <div className="notif-detail-status-row">
              <span
                className={`ess-status-badge ${statusClass(detail.status)}`}
              >
                {statusLabel(detail.status)}
              </span>
              <span className="notif-detail-date">
                تاريخ التقديم: {formatDateTime(detail.submittedAt)}
              </span>
            </div>

            <div className="notif-detail-section">
              <h3 className="notif-detail-section-title">مقدم الطلب</h3>
              <DetailRow label="الاسم">{detail.employeeName}</DetailRow>
              <DetailRow label="الكود">{detail.employeeCode}</DetailRow>
              <DetailRow label="مكان العمل">
                {formatWorkplaceLabel(detail.branch, detail.department) || "—"}
              </DetailRow>
            </div>

            <div className="notif-detail-section">
              <h3 className="notif-detail-section-title">بيانات الطلب</h3>
              {detail.fields
                ?.filter((f) => f.value != null && f.value !== "")
                .map((f) => (
                  <DetailRow key={f.label} label={f.label}>
                    {f.value}
                  </DetailRow>
                ))}
              <DetailRow label="الموافق التالي">{nextApproverLabel}</DetailRow>
            </div>

            {detail.attachmentEntityType && detail.attachmentEntityId ? (
              <AttachmentList
                entityType={detail.attachmentEntityType}
                entityId={detail.attachmentEntityId}
                showDelete={false}
                hideIfEmpty
              />
            ) : null}

            {detail.history?.length > 0 && (
              <div className="notif-detail-section">
                <h3 className="notif-detail-section-title">سجل الإجراءات</h3>
                <ul className="notif-detail-timeline">
                  {detail.history.map((item) => (
                    <li key={item.id} className="notif-detail-timeline-item">
                      <div className="notif-detail-timeline-top">
                        <strong>{item.action || "إجراء"}</strong>
                        <span>{formatDateTime(item.at)}</span>
                      </div>
                      {(item.by || item.role) && (
                        <div className="notif-detail-timeline-by">
                          {item.by && item.role
                            ? `بواسطة: ${item.by} (${item.role})`
                            : item.by
                              ? `بواسطة: ${item.by}`
                              : `الدور: ${item.role}`}
                        </div>
                      )}
                      {item.notes && (
                        <div className="notif-detail-timeline-notes">
                          {item.notes}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {notification?.message && (
              <div className="notif-detail-section">
                <h3 className="notif-detail-section-title">نص الإشعار</h3>
                <p className="notif-detail-message">{notification.message}</p>
              </div>
            )}
          </div>
        )}

        <div className="notif-detail-footer">
          <button
            type="button"
            className="ess-btn-primary"
            onClick={onClose}
          >
            إغلاق
          </button>
        </div>
      </div>
    </div>
  );
}
